import React, { useRef, useState } from "react";
import {
    Tabs, Tab, Typography, Box, IconButton, Tooltip, Chip,
    ToggleButton, ToggleButtonGroup,
    MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import { useAdminFeedbackPage, FeedbackFilter } from "./useAdminFeedbackPage";
import "./AdminFeedbackPage.scss";

const AdminFeedbackPage: React.FC = () => {
    // Snackbar
    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({ message: "", state: SnackTypes.success });
    const notify = (message: string, isError: boolean) => {
        setSnackBarParams({ message, state: isError ? SnackTypes.error : SnackTypes.success });
        snackBarRef.current?.showSnackbar();
    };

    const {
        activeTab, setActiveTab,
        filter, setFilter,
        isLoading,
        ratingFilter, setRatingFilter,
        categorySuggestions, ggReviews, writeToUsMessages,
        fetchCategorySuggestions, fetchGGReviews, fetchWriteToUsMessages,
        markCategorySuggestionRead, markGGReviewRead, markWriteToUsRead,
        clearAll,
    } = useAdminFeedbackPage(notify);

    const [fetchType, setFetchType] = useState<string | null>(null);

    const handleFetch = () => {
        const label = activeTab === 0 ? 'suggestions' : activeTab === 1 ? 'reviews' : 'messages';
        setFetchType(label);
        if (activeTab === 0) fetchCategorySuggestions();
        else if (activeTab === 1) fetchGGReviews();
        else fetchWriteToUsMessages();
    };

    const clearList = () => {
        clearAll();
        setFetchType(null);
        notify("List cleared", false);
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            notify(`${fieldName} copied to clipboard`, false);
        } catch {
            notify(`Failed to copy ${fieldName}`, true);
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    };

    return (
        <Box className="admin-feedback-page">
            {/* Page Header */}
            <Box sx={{
                background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                borderRadius: 2, p: 3, mb: 2, color: "#fff",
            }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    <i className="fas fa-comments" style={{ marginRight: 10 }}></i>
                    Customer Feedback
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                    View and manage category suggestions, platform reviews, and contact messages
                </Typography>
            </Box>

            {/* Content Card */}
            <Box sx={{
                background: "#fff", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden",
            }}>
                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_e, val) => { setActiveTab(val); setFetchType(null); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        background: '#f8fafc',
                        px: 3,
                        '& .MuiTabs-indicator': {
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            height: 3,
                            borderRadius: '2px 2px 0 0'
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#64748b',
                            '&.Mui-selected': {
                                color: '#1e293b'
                            }
                        }
                    }}
                >
                    <Tab label="📋 Category Suggestions" />
                    <Tab label="⭐ GG Reviews" />
                    <Tab label="📩 Write To Us" />
                </Tabs>

                {/* Controls */}
                <Box sx={{
                    display: "flex", gap: 2, p: 2, flexWrap: "wrap", alignItems: "center",
                    background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
                }}>
                    <ToggleButtonGroup
                        value={filter}
                        exclusive
                        onChange={(_e, val) => { if (val !== null) setFilter(val as FeedbackFilter); }}
                        size="small"
                        sx={{
                            "& .MuiToggleButton-root": {
                                textTransform: "none", fontWeight: 500, px: 2,
                                "&.Mui-selected": { background: "#3b82f6", color: "#fff", "&:hover": { background: "#2563eb" } },
                            },
                        }}
                    >
                        <ToggleButton value="unread">Unread</ToggleButton>
                        <ToggleButton value="read">Read</ToggleButton>
                        <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>

                    {activeTab === 1 && (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Filter by Rating</InputLabel>
                            <Select
                                value={ratingFilter ?? ""}
                                label="Filter by Rating"
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setRatingFilter(val === "" ? null : Number(val));
                                }}
                            >
                                <MenuItem value="">None</MenuItem>
                                {[1, 2, 3, 4, 5].map(r => (
                                    <MenuItem key={r} value={r}>{"⭐".repeat(r)} ({r})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <GGLoaderButton
                        isLoading={isLoading}
                        onClick={handleFetch}
                        variant="contained"
                        size="small"
                        sx={{
                            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                            "&:hover": { background: "linear-gradient(135deg, #2563eb, #1e40af)" },
                        }}
                    >
                        Fetch
                    </GGLoaderButton>
                </Box>

                {/* Status Bar */}
                {fetchType && (
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        mx: 2, mt: 2, p: 1.5, borderRadius: 2,
                        background: activeTab === 0 ? '#e3f2fd' : activeTab === 1 ? '#f3e8ff' : '#e8f5e8',
                        border: `1px solid ${activeTab === 0 ? '#2196f3' : activeTab === 1 ? '#8b5cf6' : '#4caf50'}`,
                        minHeight: '40px'
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {filter === 'unread' ? '🔴 Unread' : filter === 'read' ? '✅ Read' : '📋 All'} {fetchType} • {
                                activeTab === 0 ? categorySuggestions.length :
                                activeTab === 1 ? ggReviews.length : writeToUsMessages.length
                            } items
                        </Typography>
                        <GGLoaderButton
                            variant="outlined"
                            onClick={clearList}
                            isLoading={false}
                            size="small"
                            sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                        >
                            Clear
                        </GGLoaderButton>
                    </Box>
                )}

                {/* Results */}
                <Box sx={{ p: 2 }}>
                    {activeTab === 0 && (
                        <Box className="feedback-cards-grid">
                            {categorySuggestions.length === 0 && !isLoading && (
                                <Typography sx={{ color: "#94a3b8", textAlign: "center", py: 4, gridColumn: "1 / -1" }}>
                                    No category suggestions found. Click "Fetch" to load.
                                </Typography>
                            )}
                            {categorySuggestions.map((item) => (
                                <Box key={item._id} className="feedback-card" sx={{ borderLeft: `4px solid ${item.isRead ? "#94a3b8" : "#3b82f6"}` }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {item.categoryName}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                            <Chip
                                                label={item.isRead ? "Read" : "Unread"}
                                                size="small"
                                                sx={{
                                                    background: item.isRead ? "#f1f5f9" : "#dbeafe",
                                                    color: item.isRead ? "#64748b" : "#1d4ed8",
                                                    fontWeight: 600, fontSize: "0.75rem",
                                                }}
                                            />
                                            {!item.isRead && (
                                                <Tooltip title="Mark as read">
                                                    <IconButton size="small" onClick={() => markCategorySuggestionRead(item._id)}
                                                        sx={{ color: "#3b82f6" }}>
                                                        <MarkEmailReadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                    <DataRow label="Subcategory" value={item.subcategoryName} onCopy={copyToClipboard} />
                                    <DataRow label="Email" value={item.email} onCopy={copyToClipboard} />
                                    {item.comments && <DataRow label="Comments" value={item.comments} onCopy={copyToClipboard} />}
                                    <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
                                        {formatDate(item.createdAt)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box className="feedback-cards-grid">
                            {ggReviews.length === 0 && !isLoading && (
                                <Typography sx={{ color: "#94a3b8", textAlign: "center", py: 4, gridColumn: "1 / -1" }}>
                                    No reviews found. Click "Fetch" to load.
                                </Typography>
                            )}
                            {ggReviews.map((item) => (
                                <Box key={item._id} className="feedback-card" sx={{ borderLeft: `4px solid ${item.isRead ? "#94a3b8" : "#8b5cf6"}` }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                                {"⭐".repeat(item.rating)}
                                            </Typography>
                                            <Chip label={`${item.rating}/5`} size="small"
                                                sx={{ background: "#fef3c7", color: "#92400e", fontWeight: 700, fontSize: "0.75rem" }} />
                                        </Box>
                                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                            <Chip
                                                label={item.isRead ? "Read" : "Unread"}
                                                size="small"
                                                sx={{
                                                    background: item.isRead ? "#f1f5f9" : "#f3e8ff",
                                                    color: item.isRead ? "#64748b" : "#7c3aed",
                                                    fontWeight: 600, fontSize: "0.75rem",
                                                }}
                                            />
                                            {!item.isRead && (
                                                <Tooltip title="Mark as read">
                                                    <IconButton size="small" onClick={() => markGGReviewRead(item._id)}
                                                        sx={{ color: "#8b5cf6" }}>
                                                        <MarkEmailReadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                    <DataRow label="User Type" value={item.submittedByUserType} onCopy={copyToClipboard} />
                                    <DataRow label="User ID" value={item.userId} onCopy={copyToClipboard} />
                                    <DataRow label="Comments" value={item.comments} onCopy={copyToClipboard} />
                                    <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
                                        {formatDate(item.createdAt)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {activeTab === 2 && (
                        <Box className="feedback-cards-grid">
                            {writeToUsMessages.length === 0 && !isLoading && (
                                <Typography sx={{ color: "#94a3b8", textAlign: "center", py: 4, gridColumn: "1 / -1" }}>
                                    No messages found. Click "Fetch" to load.
                                </Typography>
                            )}
                            {writeToUsMessages.map((item) => (
                                <Box key={item._id} className="feedback-card" sx={{ borderLeft: `4px solid ${item.isRead ? "#94a3b8" : "#10b981"}` }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {item.fullName}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                            <Chip
                                                label={item.isRead ? "Read" : "Unread"}
                                                size="small"
                                                sx={{
                                                    background: item.isRead ? "#f1f5f9" : "#d1fae5",
                                                    color: item.isRead ? "#64748b" : "#065f46",
                                                    fontWeight: 600, fontSize: "0.75rem",
                                                }}
                                            />
                                            {!item.isRead && (
                                                <Tooltip title="Mark as read">
                                                    <IconButton size="small" onClick={() => markWriteToUsRead(item._id)}
                                                        sx={{ color: "#10b981" }}>
                                                        <MarkEmailReadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                    <DataRow label="Email" value={item.email} onCopy={copyToClipboard} />
                                    <DataRow label="Contact" value={item.contactNo} onCopy={copyToClipboard} />
                                    <DataRow label="Message" value={item.message} onCopy={copyToClipboard} />
                                    <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
                                        {formatDate(item.createdAt)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={4000} message={snackBarParams.message} />
        </Box>
    );
};

const DataRow: React.FC<{ label: string; value: string; onCopy?: (text: string, fieldName: string) => void }> = ({ label, value, onCopy }) => (
    <Box sx={{ display: "flex", gap: 1, mb: 0.5, fontSize: "0.875rem", alignItems: "center" }}>
        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, minWidth: 100 }}>{label}:</Typography>
        <Typography variant="body2" sx={{ color: "#334155", wordBreak: "break-word", flex: 1 }}>{value}</Typography>
        {onCopy && (
            <IconButton size="small" onClick={() => onCopy(value, label)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
        )}
    </Box>
);

export default AdminFeedbackPage;
